import { useState, useMemo, useEffect } from 'react'
import { Email } from '../types/email'
import { getSentEmails } from '../data/mockEmails'
import { getEmailsFromCache, Email as CachedEmail } from '../services/emailService'
import { useAuth } from '../contexts/AuthContext'
import EmailList from '../components/Email/EmailList'
import EmailView from '../components/Email/EmailView'

interface SentProps {
  searchQuery: string
  refreshTrigger?: number
}

// Convert cached email to app email format
function convertCachedEmail(cached: CachedEmail): Email {
  return {
    id: cached.id,
    accountId: cached.accountId,
    accountEmail: cached.accountEmail,
    accountColor: cached.accountColor,
    from: {
      name: cached.fromName || cached.from,
      email: cached.from
    },
    to: [{
      name: cached.to,
      email: cached.to
    }],
    subject: cached.subject,
    body: cached.body,
    preview: cached.snippet || cached.body.substring(0, 100),
    date: cached.date instanceof Date ? cached.date : new Date(cached.date),
    isRead: cached.isRead,
    isStarred: false,
    folder: cached.folder as Email['folder']
  }
}

export default function Sent({ searchQuery, refreshTrigger = 0 }: SentProps) {
  const { user } = useAuth()
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [cachedEmails, setCachedEmails] = useState<Email[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch emails from cache when component mounts or refresh is triggered
  useEffect(() => {
    const loadEmails = async () => {
      if (!user) {
        setCachedEmails([])
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const emails = await getEmailsFromCache(user.uid, 'SENT', 50)
        setCachedEmails(emails.map(convertCachedEmail))
      } catch (error) {
        console.error('Failed to load emails from cache:', error)
      } finally {
        setLoading(false)
      }
    }

    loadEmails()
  }, [user, refreshTrigger])

  const emails = useMemo(() => {
    // Use cached emails if available, otherwise fall back to mock data
    const allEmails = cachedEmails.length > 0 ? cachedEmails : getSentEmails()

    if (!searchQuery.trim()) return allEmails

    const query = searchQuery.toLowerCase()
    return allEmails.filter(
      (email) =>
        email.subject.toLowerCase().includes(query) ||
        email.to.some(
          (t) =>
            t.name.toLowerCase().includes(query) ||
            t.email.toLowerCase().includes(query)
        ) ||
        email.preview.toLowerCase().includes(query)
    )
  }, [searchQuery, cachedEmails])

  return (
    <div className="h-full flex">
      {/* Email List */}
      <div className="w-96 border-r border-gray-200 flex flex-col bg-white">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Sent</h2>
          <p className="text-sm text-gray-500">
            {loading ? 'Loading...' : `${emails.length} ${emails.length === 1 ? 'message' : 'messages'}`}
          </p>
        </div>
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <EmailList
            emails={emails}
            selectedEmailId={selectedEmail?.id || null}
            onSelectEmail={setSelectedEmail}
          />
        )}
      </div>

      {/* Email View */}
      <EmailView email={selectedEmail} />
    </div>
  )
}
